import { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { Camera, CameraOff, ShieldAlert } from 'lucide-react';
import { Button } from './UI';

/**
 * Camera QR scanner for the browser.
 *
 * Reads frames from getUserMedia onto a canvas and decodes them with jsQR, so it
 * works in every modern browser — including iOS Safari, which has no native
 * BarcodeDetector. Cameras are only exposed on secure origins, so this needs
 * HTTPS (a Vercel URL is fine) or localhost.
 *
 * Props mirror the app's scanner: `onScan(code)`, `busy`, `hint`.
 */
export default function QrScanner({ onScan, busy = false, hint }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const frameRef = useRef(0);
  const lastRef = useRef({ code: null, at: 0 });
  const busyRef = useRef(busy);

  const [status, setStatus] = useState('starting'); // starting | live | denied | unsupported | insecure | error
  const typing = false;

  // The frame loop outlives renders, so it reads `busy` through a ref.
  useEffect(() => { busyRef.current = busy; }, [busy]);

  /** Ignores the same code read again within a moment — the camera keeps firing. */
  const emit = (code) => {
    const now = Date.now();
    if (!code || busyRef.current) return;
    if (lastRef.current.code === code && now - lastRef.current.at < 2500) return;
    lastRef.current = { code, at: now };
    onScan(code);
  };

  useEffect(() => {
    if (typing) return undefined;
    let cancelled = false;

    if (!window.isSecureContext) { setStatus('insecure'); return undefined; }
    if (!navigator.mediaDevices?.getUserMedia) { setStatus('unsupported'); return undefined; }

    const tick = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (cancelled || !video || !canvas) return;

      if (video.readyState >= video.HAVE_ENOUGH_DATA && video.videoWidth) {
        // Decode a downscaled frame: plenty for a QR, and far cheaper per tick.
        const scale = Math.min(1, 640 / video.videoWidth);
        const w = Math.round(video.videoWidth * scale);
        const h = Math.round(video.videoHeight * scale);
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(video, 0, 0, w, h);
        const found = jsQR(ctx.getImageData(0, 0, w, h).data, w, h, { inversionAttempts: 'dontInvert' });
        if (found?.data) emit(found.data);
      }
      frameRef.current = requestAnimationFrame(tick);
    };

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;

        const video = videoRef.current;
        video.srcObject = stream;
        // iOS Safari will not play inline, or at all, without these.
        video.setAttribute('playsinline', '');
        video.muted = true;
        await video.play();

        setStatus('live');
        frameRef.current = requestAnimationFrame(tick);
      } catch (err) {
        if (cancelled) return;
        if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') setStatus('denied');
        else if (err?.name === 'NotFoundError' || err?.name === 'OverconstrainedError') setStatus('unsupported');
        else setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
    // `emit` only reads refs; restarting the camera on its identity would flicker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typing]);

  const problem = {
    insecure: 'The camera needs a secure (https) page. Open this site over https.',
    unsupported: 'No camera was found on this device.',
    denied: 'Camera access was blocked. Allow it in your browser\'s site settings (the lock icon in the address bar), then reload.',
    error: 'The camera could not be started. Close other apps using the camera and reload.',
  }[status];

  return (
    <div style={{ width: '100%' }}>
      {!typing && !problem && (
        <div className="gl-scan" style={{
          position: 'relative', width: '100%', maxWidth: 460, margin: '0 auto',
          aspectRatio: '1 / 1', borderRadius: 'var(--r-xl)', overflow: 'hidden',
          background: '#050507', border: '1px solid var(--border)',
        }}>
          <video ref={videoRef} playsInline muted
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <canvas ref={canvasRef} style={{ display: 'none' }} />

          {/* Aiming frame */}
          <div aria-hidden style={{
            position: 'absolute', inset: '17%', borderRadius: 'var(--r-lg)',
            boxShadow: '0 0 0 9999px rgba(5,5,7,0.46)',
          }}>
            {['tl', 'tr', 'bl', 'br'].map((c) => (
              <span key={c} style={{
                position: 'absolute', width: 30, height: 30, borderColor: 'var(--gold-400)', borderStyle: 'solid',
                borderWidth: `${c[0] === 't' ? 3 : 0}px ${c[1] === 'r' ? 3 : 0}px ${c[0] === 'b' ? 3 : 0}px ${c[1] === 'l' ? 3 : 0}px`,
                [c[0] === 't' ? 'top' : 'bottom']: 0, [c[1] === 'l' ? 'left' : 'right']: 0,
                borderRadius: 6,
              }} />
            ))}
            {status === 'live' && <span className="gl-scan-sweep" />}
          </div>

          {status === 'starting' && (
            <div style={{
              position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
              color: 'var(--text-mid)', fontSize: 13,
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Camera size={16} /> Starting the camera…</span>
            </div>
          )}

          {busy && (
            <div style={{
              position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
              background: 'rgba(5,5,7,0.55)', color: 'var(--gold-300)', fontSize: 13.5, fontWeight: 600,
            }}>
              Checking…
            </div>
          )}
        </div>
      )}

      {!typing && problem && (
        <div style={{
          display: 'flex', gap: 12, alignItems: 'flex-start', padding: 16, maxWidth: 460, margin: '0 auto',
          background: 'var(--warn-dim)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: 'var(--r)',
        }}>
          {status === 'denied'
            ? <ShieldAlert size={18} color="var(--warn)" style={{ flexShrink: 0, marginTop: 1 }} />
            : <CameraOff size={18} color="var(--warn)" style={{ flexShrink: 0, marginTop: 1 }} />}
          <p style={{ fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.6 }}>{problem}</p>
        </div>
      )}

      {!typing && !problem && (
        <p style={{ fontSize: 12.5, color: 'var(--text-mid)', textAlign: 'center', marginTop: 12 }}>
          {hint || 'Hold the QR code inside the frame'}
        </p>
      )}

      {status === 'denied' && (
        <div style={{ textAlign: 'center', marginTop: 10 }}>
          <Button variant="subtle" size="sm" onClick={() => window.location.reload()}>Reload after allowing the camera</Button>
        </div>
      )}
    </div>
  );
}
